import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchTournaments } from "../api/tournamentApi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  LogoTrophy,
  LogoCalendar,
  LogoActivity,
  LogoList,
  LogoMedal,
  LogoUsers,
  LogoMail,
} from "../components/CardLogos";
import "../css/Tournaments.css";

const HERO_IMAGE = "https://images.unsplash.com/photo-1461896836934-2e5cb9729f31?w=1920&q=80";
const CARD_IMAGES = [
  "https://media.istockphoto.com/id/1159866054/photo/athlete-holding-trophy-cup-above-head-in-stadium.jpg?s=612x612&w=0&k=20&c=MyiLVeTJh2dv1GhrjJXWszDi7k4YcJy-DWQay1WMZGA=",
  "https://png.pngtree.com/thumb_back/fh260/background/20240611/pngtree-a-winner-gold-trophy-on-stage-image_15866509.jpg",
  "https://static.vecteezy.com/system/resources/thumbnails/065/781/360/small/golden-trophy-is-raised-by-hands-free-photo.jpeg",
  "https://media.istockphoto.com/id/1159866069/photo/athlete-holding-trophy-cup-above-head-in-stadium.jpg?s=612x612&w=0&k=20&c=fDBLI9RhkWa6JQ3902-Obtz6hIaN5iDTI5WIyg3UILA=",
];

function Tournaments() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hoverCardId, setHoverCardId] = useState(null);
  const [registerPromptOpen, setRegisterPromptOpen] = useState(false);

  useEffect(() => {
    fetchTournaments().then(setData);
  }, []);

  if (!data) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    const tournamentDate = new Date(dateString);
    const diffTime = tournamentDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getCardImage = (index) => CARD_IMAGES[index % CARD_IMAGES.length];
  const getTournamentLocation = (tournament) =>
    tournament?.venue?.city || tournament?.venue?.stadium || tournament?.location || "Location TBA";

  const handleRegisterClick = (event) => {
    event.stopPropagation();
    setRegisterPromptOpen(true);
  };

  const contactEmail = typeof data.contactSupport?.email === "string"
    ? data.contactSupport.email.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").trim()
    : (data.contactSupport?.email || "");
  const contactPhone = data.contactSupport?.phone || "";

  return (
    <div className="tournaments-page page-enter">
      <Navbar />

      {/* Hero */}
      <section className="tournaments-hero hero-with-image section-geom" style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
        <div className="hero-overlay" />
        <div className="hero-overlay-blend" />
        <div className="container tournaments-hero-inner">
          <p className="tournaments-hero-label">Tournaments</p>
          <h1 className="tournaments-hero-title">{data.pageTitle}</h1>
          <p className="tournaments-hero-subtitle">{data.description}</p>
          <div className="tournaments-hero-strip">
            <a href="#stats" className="tournaments-strip-item">
              <LogoTrophy className="strip-icon" aria-hidden />
              <span>Stats</span>
            </a>
            <span className="tournaments-strip-dot" aria-hidden="true" />
            <a href="#events" className="tournaments-strip-item">
              <LogoCalendar className="strip-icon" aria-hidden />
              <span>Events</span>
            </a>
            <span className="tournaments-strip-dot" aria-hidden="true" />
            <a href="#how" className="tournaments-strip-item">
              <span>How to join</span>
            </a>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section id="stats" className="tournaments-stats-strip">
        <div className="container tournaments-stats-inner">
          <div className="tournaments-stat">
            <LogoTrophy className="tournaments-stat-icon" aria-hidden />
            <span className="tournaments-stat-value">{data.tournamentStats.totalTournaments}</span>
            <span className="tournaments-stat-label">Total</span>
          </div>
          <div className="tournaments-stat">
            <LogoCalendar className="tournaments-stat-icon" aria-hidden />
            <span className="tournaments-stat-value">{data.tournamentStats.upcoming}</span>
            <span className="tournaments-stat-label">Upcoming</span>
          </div>
          <div className="tournaments-stat">
            <LogoMedal className="tournaments-stat-icon" aria-hidden />
            <span className="tournaments-stat-value">{data.tournamentStats.completed}</span>
            <span className="tournaments-stat-label">Completed</span>
          </div>
          <div className="tournaments-stat">
            <LogoUsers className="tournaments-stat-icon" aria-hidden />
            <span className="tournaments-stat-value">{data.tournamentStats.totalParticipants}</span>
            <span className="tournaments-stat-label">Participants</span>
          </div>
          <div className="tournaments-stat">
            <LogoActivity className="tournaments-stat-icon" aria-hidden />
            <span className="tournaments-stat-value">{data.tournamentStats.sportsCovered}</span>
            <span className="tournaments-stat-label">Sports</span>
          </div>
        </div>
      </section>

      {/* Upcoming events */}
      <section id="events" className="tournaments-section tournaments-events">
        <div className="container">
          <h2 className="tournaments-section-title">Upcoming tournaments</h2>
          {data.upcomingTournaments.length === 0 ? (
            <div className="tournaments-empty card-reveal">
              <LogoCalendar className="card-logo tournaments-empty-icon" aria-hidden />
              <h3>No upcoming tournaments</h3>
              <p>Check back soon for new events and competitions.</p>
            </div>
          ) : (
            <div className="tournaments-events-grid">
              {data.upcomingTournaments.map((tournament, index) => {
                const daysUntil = getDaysUntil(tournament.schedule.startDate);
                const isUpcoming = daysUntil > 0;
                const cardImage = getCardImage(index);
                const showOverlay = hoverCardId === tournament.id;
                return (
                  <div
                    key={tournament.id}
                    className="tournaments-event-card tournaments-event-card-upcoming card-reveal"
                    onMouseEnter={() => setHoverCardId(tournament.id)}
                    onMouseLeave={() => setHoverCardId(null)}
                  >
                    <div
                      className="tournaments-event-bg"
                      style={{ backgroundImage: `url(${cardImage})` }}
                    >
                      <div className="tournaments-event-overlay" aria-hidden="true" />
                      <div className="tournaments-event-header">
                        <span className="tournaments-status-pill upcoming">
                          <LogoTrophy className="card-logo tournaments-event-icon" aria-hidden />
                          Upcoming
                        </span>
                        <span className={`tournaments-event-badge ${isUpcoming ? "upcoming" : "today"}`}>
                          {isUpcoming ? (daysUntil === 1 ? "Tomorrow" : `${daysUntil} days`) : "Today"}
                        </span>
                      </div>
                      <div className="tournaments-event-content">
                        <div className="tournaments-card-ring upcoming" aria-hidden="true" />
                        <h3 className="tournaments-event-name">{tournament.name}</h3>
                        <p className="tournaments-event-meta">{formatDate(tournament.schedule.startDate)}</p>
                        <div className="tournaments-event-tags">
                          <span className="tournaments-event-tag tournaments-event-tag-sport">{tournament.sport}</span>
                          <span className="tournaments-event-tag tournaments-event-tag-location">{getTournamentLocation(tournament)}</span>
                        </div>
                        <p className="tournaments-event-hint">Starts in {isUpcoming ? (daysUntil === 1 ? "1 day" : `${daysUntil} days`) : "today"}</p>
                      </div>
                      <div className={`tournaments-event-actions ${showOverlay ? "visible" : ""}`}>
                        <button
                          type="button"
                          className="tournaments-btn tournaments-btn-primary"
                          onClick={(e) => handleRegisterClick(e)}
                        >
                          Register
                        </button>
                        <button
                          type="button"
                          className="tournaments-btn tournaments-btn-ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setHoverCardId(null);
                          }}
                        >
                          Close
                        </button>
                      </div>
                      <div className="tournaments-event-bottom-glow" aria-hidden="true" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Ongoing events */}
      <section className="tournaments-section tournaments-events">
        <div className="container">
          <h2 className="tournaments-section-title">Ongoing tournaments</h2>
          {data.ongoingTournaments?.length === 0 ? (
            <div className="tournaments-empty card-reveal">
              <LogoActivity className="card-logo tournaments-empty-icon" aria-hidden />
              <h3>No ongoing tournaments</h3>
              <p>Current live tournaments will appear here.</p>
            </div>
          ) : (
            <div className="tournaments-events-grid">
              {data.ongoingTournaments?.map((tournament, index) => {
                const cardImage = getCardImage(index + (data.upcomingTournaments?.length || 0));
                return (
                  <div
                    key={`ongoing-${tournament.id}`}
                    className="tournaments-event-card tournaments-event-card-live card-reveal"
                  >
                    <div
                      className="tournaments-event-bg"
                      style={{ backgroundImage: `url(${cardImage})` }}
                    >
                      <div className="tournaments-event-overlay" aria-hidden="true" />
                      <div className="tournaments-event-header">
                        <span className="tournaments-status-pill live">
                          <span className="tournaments-live-dot" aria-hidden="true" />
                          Live Now
                        </span>
                        <span className="tournaments-event-badge today">Ongoing</span>
                      </div>
                      <div className="tournaments-event-content">
                        <div className="tournaments-card-ring live" aria-hidden="true" />
                        <h3 className="tournaments-event-name">{tournament.name}</h3>
                        <p className="tournaments-event-meta">{formatDate(tournament.schedule.startDate)}</p>
                        <div className="tournaments-event-tags">
                          <span className="tournaments-event-tag tournaments-event-tag-sport">{tournament.sport}</span>
                          <span className="tournaments-event-tag tournaments-event-tag-location">{getTournamentLocation(tournament)}</span>
                        </div>
                        <p className="tournaments-event-hint">Live event in progress</p>
                      </div>
                      <div className="tournaments-live-sweep" aria-hidden="true" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* How to participate — timeline */}
      <section id="how" className="tournaments-section tournaments-how">
        <div className="container">
          <h2 className="tournaments-section-title">How to participate</h2>
          <p className="tournaments-section-desc">Follow these steps to join our tournaments.</p>
          <div className="tournaments-steps">
            <div className="tournaments-step card-reveal">
              <span className="tournaments-step-num" aria-hidden="true">1</span>
              <div className="tournaments-step-content">
                <LogoList className="card-logo tournaments-step-icon" />
                <h4>Register</h4>
                <p>Register early to secure your spot.</p>
              </div>
            </div>
            <div className="tournaments-step card-reveal">
              <span className="tournaments-step-num" aria-hidden="true">2</span>
              <div className="tournaments-step-content">
                <LogoMedal className="card-logo tournaments-step-icon" />
                <h4>Prizes & recognition</h4>
                <p>Compete for prizes and certificates.</p>
              </div>
            </div>
            <div className="tournaments-step card-reveal">
              <span className="tournaments-step-num" aria-hidden="true">3</span>
              <div className="tournaments-step-content">
                <LogoUsers className="card-logo tournaments-step-icon" />
                <h4>Participation</h4>
                <p>Open to all age groups and skill levels.</p>
              </div>
            </div>
            <div className="tournaments-step card-reveal">
              <span className="tournaments-step-num" aria-hidden="true">4</span>
              <div className="tournaments-step-content">
                <LogoMail className="card-logo tournaments-step-icon" />
                <h4>Contact</h4>
                <p>Reach out for more information.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rules & Benefits */}
      <section className="tournaments-section tournaments-rules-benefits">
        <div className="container tournaments-rb-grid">
          <div className="tournaments-rules-card card-reveal">
            <div className="tournaments-card-accent" aria-hidden="true" />
            <h2 className="tournaments-block-title">Rules</h2>
            <ul className="tournaments-list">
              {data.rules.map((rule, i) => (
                <li key={i}>{rule}</li>
              ))}
            </ul>
          </div>
          <div className="tournaments-benefits-card card-reveal">
            <div className="tournaments-card-accent" aria-hidden="true" />
            <h2 className="tournaments-block-title">Benefits</h2>
            <ul className="tournaments-list tournaments-list-check">
              {data.benefits.map((benefit, i) => (
                <li key={i}>{benefit}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contact strip */}
      <section className="tournaments-contact-strip">
        <div className="container tournaments-contact-inner">
          <LogoMail className="card-logo tournaments-contact-icon" aria-hidden />
          <div className="tournaments-contact-text">
            <h3>Need help?</h3>
            <p>For tournament-related queries, get in touch.</p>
          </div>
          <div className="tournaments-contact-links">
            <a href={`mailto:${contactEmail}`} className="tournaments-contact-btn">{contactEmail || "Email"}</a>
            <a href={`tel:${contactPhone}`} className="tournaments-contact-btn">{contactPhone || "Phone"}</a>
          </div>
        </div>
      </section>

      <Footer organizationName="Giri Nestham Sports Welfare Foundation" />

      {registerPromptOpen ? (
        <div
          className="tournaments-register-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tournaments-register-modal-title"
          onClick={() => setRegisterPromptOpen(false)}
        >
          <div
            className="tournaments-register-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="tournaments-register-modal-title">Tournament registration</h3>
            <p>
              Registration is handled by our admin team. Please contact us to register
              for a tournament, or visit the contact page for email and phone details.
            </p>
            <div className="tournaments-register-modal-actions">
              <button
                type="button"
                className="tournaments-contact-btn"
                onClick={() => {
                  setRegisterPromptOpen(false);
                  navigate("/contact");
                }}
              >
                Contact admin
              </button>
              <button
                type="button"
                className="tournaments-register-modal-close"
                onClick={() => setRegisterPromptOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Tournaments;
